import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, ChevronDown, ChevronUp, Plus, Pencil, Check, X, Trash2, AlertTriangle, History, Search, Gift } from "lucide-react";
import ExternalTripsModal from "@/components/ExternalTripsModal";

const MONTH_OPTIONS = [3, 6, 7, 12];
const METHODS = ["zelle", "venmo", "cashapp", "cash", "check", "stripe", "other"];

const fmtDate = (iso) => (iso ? new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "2-digit" }) : "—");
const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toFixed(2)}`;

const statusChip = (t) => {
  const s = t.settlement_status;
  if (s === "paid") return { label: `paid${t.method ? ` · ${t.method}` : ""}`, cls: "text-emerald-300 border-emerald-400/40" };
  if (s === "approved") return { label: "approved", cls: "text-[#D4AF37] border-[#D4AF37]/40" };
  if (s === "pending") return { label: "pending", cls: "text-[#A1A1AA] border-white/20" };
  if (s === "unpaid") return { label: "unpaid", cls: "text-red-300 border-red-400/40" };
  return { label: "unsettled", cls: "text-[#71717A] border-white/10" };
};

/** Confirm-with-force helper: a 409 from the API means "double-payment / paid record" — ask, then retry with force. */
async function withForce(call, confirmPrefix = "") {
  try { return await call(false); }
  catch (e) {
    if (e?.response?.status === 409) {
      if (window.confirm(`${confirmPrefix}${e.response.data?.detail}\n\nProceed anyway?`)) return call(true);
      return null;
    }
    throw e;
  }
}

/** Driver payroll spreadsheet with full administrative control (edit / move / remove / pull / adjustments / history). */
export default function DriverPayrollSheet() {
  const [months, setMonths] = useState(7);
  const [data, setData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [showExternal, setShowExternal] = useState(false);
  const [editing, setEditing] = useState(null);      // { key, amount, status, paid_date, method }
  const [pullFor, setPullFor] = useState(null);      // driver_id
  const [adjustFor, setAdjustFor] = useState(null);  // driver_id
  const [historyFor, setHistoryFor] = useState(null); // driver_id

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [r, d] = await Promise.all([api.get("/payouts/report", { params: { months } }), api.get("/drivers")]);
      setData(r.data);
      setDrivers((d.data || []).map(x => ({ driver_id: x.driver_id, name: x.name })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payroll report");
    } finally { setBusy(false); }
  }, [months]);

  useEffect(() => { reload(); }, [reload]);

  // ── Edit & correct (pay / status / paid on / method) ──
  const startEdit = (t) => setEditing({
    key: t.booking_id, amount: Number(t.amount).toFixed(2),
    status: t.settlement_status === "paid" ? "paid" : (t.settlement_status === "unsettled" ? "unsettled" : "approved"),
    paid_date: t.paid_date || "", method: t.method || "zelle",
  });
  const saveEdit = async (t) => {
    const amt = parseFloat(editing.amount);
    if (!Number.isFinite(amt) || (amt === 0 && editing.status !== "unsettled")) { toast.error("Enter a valid amount"); return; }
    const payload = { amount: amt, status: editing.status, method: editing.method,
      paid_date: editing.status === "paid" ? (editing.paid_date || null) : null };
    const r = await withForce((force) => api.patch(`/payouts/admin/entry/${t.booking_id}`, { ...payload, force }))
      .catch(e => { toast.error(e?.response?.data?.detail || "Update failed"); });
    if (!r) return;
    toast.success(`Saved · ${money(amt)} · ${editing.status}`);
    setEditing(null);
    reload();
  };

  // ── Move to another driver ──
  const moveEntry = async (t, driver_id) => {
    if (!driver_id) return;
    try {
      const r = await api.post("/payouts/admin/move", { ref: t.booking_id, driver_id });
      toast.success(`Moved to ${r.data.driver_name}`);
      if (r.data.warning) toast.warning(r.data.warning);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  // ── Remove ──
  const removeEntry = async (t) => {
    if (!window.confirm(`Remove ${t.confirmation_code} from this ledger?`)) return;
    const r = await withForce((force) => api.post("/payouts/admin/remove", { ref: t.booking_id, force }), "")
      .catch(e => { toast.error(e?.response?.data?.detail || "Remove failed"); });
    if (!r) return;
    toast.success("Entry removed");
    reload();
  };

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Driver", "Date", "Source", "Trip", "Route", "Driver Pay", "Status", "Method", "Paid On"].join(",")];
    data.drivers.forEach(d => {
      d.trips.forEach(t => {
        lines.push([
          esc(d.driver_name), esc((t.pickup_datetime || "").slice(0, 10)), esc(t.source || "delux"), esc(t.confirmation_code),
          esc(`${t.pickup_address || ""} -> ${t.dropoff_address || ""}`),
          t.amount.toFixed(2), esc(t.settlement_status), esc(t.method || ""), esc(t.paid_date || ""),
        ].join(","));
      });
      lines.push([esc(`${d.driver_name} TOTAL`), "", "", "", `${d.trip_count} trips`, d.total_amount.toFixed(2),
        `paid ${d.total_paid.toFixed(2)} / owed ${d.total_owed.toFixed(2)}`, "", ""].join(","));
    });
    lines.push(["GRAND TOTAL", "", "", "", `${data.trip_count} trips`, data.grand_total.toFixed(2),
      `paid ${data.grand_paid.toFixed(2)} / owed ${data.grand_owed.toFixed(2)}`, "", ""].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `driver-payroll-${data.start}-to-${data.end}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="driver-payroll-sheet" className="mt-12">
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <h3 className="font-serif text-xl">Driver payroll</h3>
        <div className="flex gap-1">
          {MONTH_OPTIONS.map(m => (
            <button key={m} data-testid={`payroll-months-${m}`} onClick={() => setMonths(m)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] border ${
                months === m ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#A1A1AA]"
              }`}>{m} mo</button>
          ))}
        </div>
        {data && (
          <span className="text-[11px] text-[#A1A1AA]">
            {fmtDate(data.start)} – {fmtDate(data.end)} · {data.trip_count} entries ·
            <span className="text-[#D4AF37] font-serif text-sm ml-1">${data.grand_total.toFixed(2)}</span>
            <span className="ml-2 text-emerald-300">${data.grand_paid.toFixed(2)} paid</span>
            {data.grand_owed > 0 && <span className="ml-2 text-red-300">${data.grand_owed.toFixed(2)} owed</span>}
          </span>
        )}
        <button data-testid="payroll-add-external" onClick={() => setShowExternal(true)}
          className="btn-ghost !py-1.5 !px-3 text-[10px] ml-auto"><Plus size={12}/>Add external trips</button>
        <button data-testid="payroll-export-csv" onClick={exportCsv} disabled={!data || busy}
          className="btn-ghost !py-1.5 !px-3 text-[10px]"><Download size={12}/>Export CSV</button>
      </div>

      {busy && !data && <div className="text-[#A1A1AA] text-sm">Loading…</div>}
      {data && data.drivers.length === 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-8 text-center text-[#71717A]" data-testid="payroll-empty">
          No completed driver trips in this period.
        </div>
      )}

      {data && data.drivers.map(d => (
        <div key={d.driver_id} className="border border-white/10 bg-[#0A0A0A] mb-3" data-testid={`payroll-driver-${d.driver_id}`}>
          <button onClick={() => setCollapsed(c => ({ ...c, [d.driver_id]: !c[d.driver_id] }))}
            data-testid={`payroll-toggle-${d.driver_id}`}
            className="w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-white/[0.03]">
            <span className="text-[#F5F2EB] text-sm">{d.driver_name}</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{d.trip_count} entries</span>
            {d.trips.some(t => t.paid_elsewhere) && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-red-300 border border-red-400/40 px-1.5 py-0.5" data-testid={`payroll-dup-warning-${d.driver_id}`}>
                <AlertTriangle size={10}/> paid elsewhere
              </span>
            )}
            <span className="ml-auto flex items-center gap-4 text-[11px]">
              <span className="text-emerald-300">${d.total_paid.toFixed(2)} paid</span>
              {d.total_owed > 0 && <span className="text-red-300">${d.total_owed.toFixed(2)} owed</span>}
              <span className="font-serif text-lg text-[#D4AF37]">${d.total_amount.toFixed(2)}</span>
              {collapsed[d.driver_id] ? <ChevronDown size={14} className="text-[#71717A]"/> : <ChevronUp size={14} className="text-[#71717A]"/>}
            </span>
          </button>

          {!collapsed[d.driver_id] && (
            <div className="border-t border-white/10">
              {/* Ledger toolbar */}
              <div className="px-4 py-2 flex items-center gap-2 flex-wrap border-b border-white/5 bg-black/20">
                <button data-testid={`payroll-pull-btn-${d.driver_id}`} onClick={() => setPullFor(pullFor === d.driver_id ? null : d.driver_id)}
                  className="btn-ghost !py-1 !px-2.5 text-[10px]"><Search size={11}/>Pull trip by ID</button>
                <button data-testid={`payroll-adjust-btn-${d.driver_id}`} onClick={() => setAdjustFor(adjustFor === d.driver_id ? null : d.driver_id)}
                  className="btn-ghost !py-1 !px-2.5 text-[10px]"><Gift size={11}/>Bonus / correction</button>
                <button data-testid={`payroll-history-btn-${d.driver_id}`} onClick={() => setHistoryFor(historyFor === d.driver_id ? null : d.driver_id)}
                  className="btn-ghost !py-1 !px-2.5 text-[10px] ml-auto"><History size={11}/>{historyFor === d.driver_id ? "Hide history" : "Edit history"}</button>
              </div>
              {pullFor === d.driver_id && <PullTripForm driver={d} onDone={() => { setPullFor(null); reload(); }}/>}
              {adjustFor === d.driver_id && <AdjustmentForm driver={d} onDone={() => { setAdjustFor(null); reload(); }}/>}
              {historyFor === d.driver_id && <EditHistory driverId={d.driver_id}/>}

              <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-2">Trip</th>
                    <th className="text-left px-2">Route</th>
                    <th className="text-right px-2">Driver pay</th>
                    <th className="text-left px-4 pl-6">Payment</th>
                    <th className="text-left px-2">Paid on</th>
                    <th className="text-left px-2">Driver</th>
                    <th className="px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {d.trips.map(t => {
                    const chip = statusChip(t);
                    const isEditing = editing?.key === t.booking_id;
                    return (
                      <React.Fragment key={t.booking_id}>
                      <tr className={`border-t border-white/5 ${t.paid_elsewhere ? "bg-red-500/5" : ""} ${t.adj_id ? "bg-[#D4AF37]/[0.04]" : ""}`} data-testid={`payroll-trip-${t.booking_id}`}>
                        <td className="px-4 py-2 text-[#A1A1AA] whitespace-nowrap">{fmtDate(t.pickup_datetime)}</td>
                        <td className="px-2 font-mono text-[#D4AF37] whitespace-nowrap">
                          {t.adj_id ? (
                            <span className={`text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 border ${t.kind === "bonus" ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>{t.kind}</span>
                          ) : t.source && t.source !== "delux" ? (
                            <span className="text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 border border-sky-400/40 text-sky-300">{t.source}</span>
                          ) : t.confirmation_code}
                          {t.pulled && <span className="ml-1 text-[8px] uppercase text-[#71717A]" title="Added manually via Pull trip">pulled</span>}
                        </td>
                        <td className="px-2 text-[#A1A1AA] truncate max-w-[240px]">
                          {t.pickup_address}{t.dropoff_address ? ` → ${t.dropoff_address}` : ""}
                          {t.paid_elsewhere && (
                            <div className="text-[9px] text-red-300 inline-flex items-center gap-1 mt-0.5" data-testid={`payroll-paid-elsewhere-${t.booking_id}`}>
                              <AlertTriangle size={10}/> Already PAID on {t.paid_elsewhere.driver_name}'s ledger{t.paid_elsewhere.paid_date ? ` (${t.paid_elsewhere.paid_date})` : ""}
                            </div>
                          )}
                        </td>
                        <td className={`px-2 text-right font-mono whitespace-nowrap ${t.amount < 0 ? "text-amber-300" : "text-[#F5F2EB]"}`}>
                          <span className="inline-flex items-center gap-1.5">
                            {money(t.amount)}
                            {t.suggested && <span className="text-[9px] text-[#71717A]" title="No settlement recorded — default 77% of trip fare">auto</span>}
                            <button data-testid={`payroll-edit-btn-${t.booking_id}`} onClick={() => isEditing ? setEditing(null) : startEdit(t)}
                              className="text-[#71717A] hover:text-[#D4AF37] transition-colors" title="Edit pay / status / paid on"><Pencil size={11}/></button>
                          </span>
                        </td>
                        <td className="px-4 pl-6 py-2 whitespace-nowrap">
                          <span className={`text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border ${chip.cls}`}>{chip.label}</span>
                        </td>
                        <td className="px-2 text-[#A1A1AA] whitespace-nowrap">{t.paid_date || "—"}</td>
                        <td className="px-2">
                          <select data-testid={`payroll-move-${t.booking_id}`} value={d.driver_id} onChange={e => moveEntry(t, e.target.value)}
                            title="Change driver — moves this entry to another ledger"
                            className="bg-black border border-white/10 text-[10px] px-1.5 py-1 max-w-[140px] text-[#A1A1AA] hover:border-[#D4AF37]/50 cursor-pointer">
                            {!drivers.some(x => x.driver_id === d.driver_id) && <option value={d.driver_id}>{d.driver_name}</option>}
                            {drivers.map(x => <option key={x.driver_id} value={x.driver_id}>{x.name}</option>)}
                          </select>
                        </td>
                        <td className="px-2 text-right">
                          <button data-testid={`payroll-remove-${t.booking_id}`} onClick={() => removeEntry(t)}
                            className="text-[#71717A] hover:text-red-400 transition-colors" title="Remove from ledger"><Trash2 size={12}/></button>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr className="bg-[#D4AF37]/5 border-t border-[#D4AF37]/20" data-testid={`payroll-edit-form-${t.booking_id}`}>
                          <td colSpan={8} className="px-4 py-2">
                            <div className="flex items-end gap-3 flex-wrap">
                              <label className="text-[9px] uppercase tracking-[0.15em] text-[#71717A]">Driver pay
                                <div className="flex items-center gap-1 mt-1"><span className="text-[#71717A]">$</span>
                                  <input autoFocus type="number" step="0.01" value={editing.amount} data-testid={`payroll-edit-input-${t.booking_id}`}
                                    onChange={e => setEditing({ ...editing, amount: e.target.value })}
                                    onKeyDown={e => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") setEditing(null); }}
                                    className="w-24 bg-black border border-[#D4AF37]/60 text-right px-1.5 py-1 text-[11px] text-[#F5F2EB] outline-none"/>
                                </div>
                              </label>
                              <label className="text-[9px] uppercase tracking-[0.15em] text-[#71717A]">Payment status
                                <select data-testid={`payroll-edit-status-${t.booking_id}`} value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })}
                                  className="block mt-1 bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB]">
                                  {!t.adj_id && !t.ext_id && <option value="unsettled">Unsettled</option>}
                                  <option value="approved">{t.ext_id ? "Unpaid" : "Approved (owed)"}</option>
                                  <option value="paid">Paid</option>
                                </select>
                              </label>
                              {editing.status === "paid" && (
                                <>
                                  <label className="text-[9px] uppercase tracking-[0.15em] text-[#71717A]">Paid on
                                    <input type="date" data-testid={`payroll-edit-paid-date-${t.booking_id}`} value={editing.paid_date} onChange={e => setEditing({ ...editing, paid_date: e.target.value })}
                                      className="block mt-1 bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB]"/>
                                  </label>
                                  <label className="text-[9px] uppercase tracking-[0.15em] text-[#71717A]">Method
                                    <select data-testid={`payroll-edit-method-${t.booking_id}`} value={editing.method} onChange={e => setEditing({ ...editing, method: e.target.value })}
                                      className="block mt-1 bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB] capitalize">
                                      {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                  </label>
                                </>
                              )}
                              <div className="flex items-center gap-2 ml-auto">
                                <button data-testid={`payroll-edit-save-${t.booking_id}`} onClick={() => saveEdit(t)} className="btn-gold !py-1 !px-3 text-[10px] inline-flex items-center gap-1"><Check size={12}/>Save</button>
                                <button data-testid={`payroll-edit-cancel-${t.booking_id}`} onClick={() => setEditing(null)} className="btn-ghost !py-1 !px-3 text-[10px] inline-flex items-center gap-1"><X size={12}/>Cancel</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                  <tr className="border-t border-white/10 bg-black/30">
                    <td colSpan={3} className="px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Subtotal · {d.trip_count} entries</td>
                    <td className="px-2 text-right font-mono text-[#D4AF37]">${d.total_amount.toFixed(2)}</td>
                    <td colSpan={4} className="px-4 pl-6 text-[10px] text-[#A1A1AA]">
                      <span className="text-emerald-300">${d.total_paid.toFixed(2)} paid</span>
                      {d.total_owed > 0 && <span className="ml-2 text-red-300">${d.total_owed.toFixed(2)} owed</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      ))}

      {showExternal && (
        <ExternalTripsModal onClose={() => setShowExternal(false)} onSaved={reload}/>
      )}
    </div>
  );
}

/** "Pull trip by ID" — add a missing trip to this driver's ledger by confirmation # / booking id. */
function PullTripForm({ driver, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await withForce((force) => api.post("/payouts/admin/pull", { code: code.trim(), driver_id: driver.driver_id, force }), "");
      if (!r) return;
      toast.success(`${r.data.confirmation_code} added to ${driver.driver_name}'s ledger`);
      if (r.data.warning) toast.warning(r.data.warning);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not pull trip"); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-4 py-2.5 flex items-center gap-2 border-b border-white/5 bg-[#D4AF37]/5" data-testid={`payroll-pull-form-${driver.driver_id}`}>
      <span className="text-[9px] uppercase tracking-[0.15em] text-[#D4AF37]">Pull trip</span>
      <input data-testid={`payroll-pull-input-${driver.driver_id}`} value={code} onChange={e => setCode(e.target.value.toUpperCase())}
        onKeyDown={e => e.key === "Enter" && submit()} placeholder="Confirmation # e.g. DLX-348A3F or booking id"
        className="flex-1 max-w-sm bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB] font-mono uppercase outline-none focus:border-[#D4AF37]/60"/>
      <button data-testid={`payroll-pull-submit-${driver.driver_id}`} onClick={submit} disabled={busy || !code.trim()} className="btn-gold !py-1 !px-3 text-[10px] disabled:opacity-40">{busy ? "…" : "Add to ledger"}</button>
      <button onClick={onDone} className="btn-ghost !py-1 !px-2 text-[10px]">Cancel</button>
    </div>
  );
}

/** Manual bonus / correction (no trip id). Negative amount = deduction. */
function AdjustmentForm({ driver, onDone }) {
  const [kind, setKind] = useState("bonus");
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [status, setStatus] = useState("approved");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt === 0) { toast.error("Enter a non-zero amount"); return; }
    setBusy(true);
    try {
      await api.post("/payouts/admin/adjustments", { driver_id: driver.driver_id, amount: amt, kind, label: label.trim() || null, status });
      toast.success(`${kind === "bonus" ? "Bonus" : "Correction"} of ${money(amt)} added for ${driver.driver_name}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add adjustment"); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-4 py-2.5 flex items-center gap-2 flex-wrap border-b border-white/5 bg-[#D4AF37]/5" data-testid={`payroll-adjust-form-${driver.driver_id}`}>
      <select data-testid={`payroll-adjust-kind-${driver.driver_id}`} value={kind} onChange={e => setKind(e.target.value)} className="bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB]">
        <option value="bonus">Bonus</option><option value="correction">Correction</option>
      </select>
      <div className="flex items-center gap-1"><span className="text-[#71717A] text-xs">$</span>
        <input data-testid={`payroll-adjust-amount-${driver.driver_id}`} type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (negative = deduct)"
          className="w-44 bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB] outline-none focus:border-[#D4AF37]/60"/>
      </div>
      <input data-testid={`payroll-adjust-label-${driver.driver_id}`} value={label} onChange={e => setLabel(e.target.value)} placeholder="Reason (e.g. Holiday bonus, Fuel reimbursement)"
        className="flex-1 min-w-[200px] bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB] outline-none focus:border-[#D4AF37]/60"/>
      <select data-testid={`payroll-adjust-status-${driver.driver_id}`} value={status} onChange={e => setStatus(e.target.value)} className="bg-black border border-white/15 px-2 py-1 text-[11px] text-[#F5F2EB]">
        <option value="approved">Owed (approved)</option><option value="paid">Already paid</option>
      </select>
      <button data-testid={`payroll-adjust-submit-${driver.driver_id}`} onClick={submit} disabled={busy} className="btn-gold !py-1 !px-3 text-[10px] disabled:opacity-40">{busy ? "…" : "Add"}</button>
      <button onClick={onDone} className="btn-ghost !py-1 !px-2 text-[10px]">Cancel</button>
    </div>
  );
}

const ACTION_LABEL = {
  edit_entry: "Edited pay", edit_external: "Edited external trip", edit_adjustment: "Edited adjustment",
  move_trip: "Moved trip", move_external: "Moved external trip", move_adjustment: "Moved adjustment",
  remove_trip: "Removed trip", remove_external: "Removed external trip", remove_adjustment: "Removed adjustment",
  pull_trip: "Pulled trip", add_adjustment: "Added bonus/correction",
};
const short = (v) => {
  if (v == null) return "—";
  if (typeof v !== "object") return String(v);
  const keep = ["amount", "status", "paid_date", "method", "driver_name", "driver_id", "kind", "label"];
  const parts = keep.filter(k => v[k] != null).map(k => `${k}: ${v[k]}`);
  return parts.length ? parts.join(" · ") : "—";
};

/** Hidden audit trail of every manual payroll change for one driver. */
function EditHistory({ driverId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/payouts/admin/history", { params: { driver_id: driverId, limit: 100 } }).then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, [driverId]);
  return (
    <div className="border-b border-white/5 bg-black/30 max-h-64 overflow-y-auto" data-testid={`payroll-history-${driverId}`}>
      {rows === null && <div className="px-4 py-2 text-[10px] text-[#71717A]">Loading history…</div>}
      {rows && rows.length === 0 && <div className="px-4 py-2 text-[10px] text-[#71717A]" data-testid={`payroll-history-empty-${driverId}`}>No manual changes recorded for this driver.</div>}
      {rows && rows.map(r => (
        <div key={r.log_id} className="px-4 py-1.5 border-t border-white/5 text-[10px] grid grid-cols-12 gap-2" data-testid={`payroll-history-row-${r.log_id}`}>
          <span className="col-span-2 text-[#71717A] whitespace-nowrap">{new Date(r.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          <span className="col-span-2 text-[#D4AF37]">{ACTION_LABEL[r.action] || r.action}</span>
          <span className="col-span-2 font-mono text-[#A1A1AA] truncate" title={r.ref}>{r.ref}</span>
          <span className="col-span-5 text-[#A1A1AA] truncate" title={`${short(r.before)} → ${short(r.after)}`}>
            {r.before && <span className="text-[#71717A]">{short(r.before)} → </span>}{short(r.after)}
            {r.note && <span className="text-[#71717A]"> · "{r.note}"</span>}
          </span>
          <span className="col-span-1 text-[#71717A] truncate text-right" title={r.by}>{(r.by || "").split("@")[0]}</span>
        </div>
      ))}
    </div>
  );
}
